import { visibleChannels } from "$lib/channel-visibility.js";
import type { Channel } from "$lib/core/types.js";
import { authState } from "$lib/core/state.svelte.js";
import { openOwned, sealOwned } from "$lib/cache/local-owner.js";

// Per-workspace "last open chat" persistence. When the user returns to a
// workspace (bare root / Chats) or reloads the app, we restore the exact chat
// they last had open — channel, human DM, OR agent session — instead of dumping
// them on the first/default channel. Keyed by workspaceId so every workspace
// restores independently. Best-effort: storage-disabled just means no restore.
//
// Superseded the channel-only `cyborg7:lastChannel:<ws>` string. `getLastChannel`
// in app.svelte.ts still reads through here, returning the id only for a channel
// ref (its callers only care about channels).

export type LastChatKind = "channel" | "dm" | "agent";

export interface LastChatRef {
  kind: LastChatKind;
  id: string;
}

// Route segment under /workspace/<ws>/ for each chat kind — matches the folders
// in src/routes/workspace/[id]/ (channel/[channelId], dm/[userId], agent/[agentId]).
const KIND_SEGMENT: Record<LastChatKind, string> = {
  channel: "channel",
  dm: "dm",
  agent: "agent",
};

function key(workspaceId: string): string {
  return `cyborg7:lastChat:${workspaceId}`;
}

function isKind(v: unknown): v is LastChatKind {
  return v === "channel" || v === "dm" || v === "agent";
}

export function rememberLastChat(workspaceId: string, ref: LastChatRef): void {
  if (!workspaceId || !ref.id) return;
  try {
    const sealed = sealOwned(authState.user?.email, JSON.stringify(ref));
    if (sealed !== undefined) localStorage.setItem(key(workspaceId), sealed);
  } catch {
    // intentional: best-effort — storage disabled means we don't restore on reload.
  }
}

// Forget the remembered chat when it is `id` (hiding a DM must not leave it as the landing target).
export function forgetLastChat(workspaceId: string, id: string): void {
  if (getLastChat(workspaceId)?.id !== id) return;
  try {
    localStorage.removeItem(key(workspaceId));
  } catch {
    // intentional: best-effort — the restore predicate (isHidden) still stops a stale key.
  }
}

export function getLastChat(workspaceId: string): LastChatRef | null {
  if (!workspaceId) return null;
  try {
    // Another person's value, or a legacy plain one, is a miss (CYBO-1846 S1).
    const raw = openOwned(authState.user?.email, localStorage.getItem(key(workspaceId)));
    if (!raw) return null;
    // Untrusted (older versions, hand-edited storage): validate before trusting.
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      isKind((parsed as Record<string, unknown>).kind) &&
      typeof (parsed as Record<string, unknown>).id === "string" &&
      (parsed as LastChatRef).id.length > 0
    ) {
      const { kind, id } = parsed as LastChatRef;
      return { kind, id };
    }
    return null;
  } catch {
    return null;
  }
}

// Absolute route path for a remembered chat (relative to origin).
export function lastChatPath(workspaceId: string, ref: LastChatRef): string {
  return `/workspace/${workspaceId}/${KIND_SEGMENT[ref.kind]}/${ref.id}`;
}

export interface RosterSnapshot {
  channels: { id: string }[];
  // `cyboId` matters since DM addressing moved off the per-run agentId: a
  // remembered chat now records the CYBO, so a roster entry has to be matchable
  // by either. Both callers already pass `workspaceState.agents`, which carries
  // it. Optional so a caller with only agentIds still type-checks.
  agents: { agentId: string; cyboId?: string | null }[];
  // Is the agent roster still being fetched? `channels.length === 0` used to stand
  // in for "nothing has loaded yet" because channels and agents landed together.
  // They no longer do: channels come from the relay and paint immediately, agents
  // are daemon-forwarded and land seconds later. Without this a remembered AGENT
  // chat resolves to "none" during that window and silently stops restoring.
  // Optional so a caller with a settled roster still type-checks.
  agentsPending?: boolean;
}

export type RestoreDecision =
  | { action: "wait" } // roster not loaded yet — retry when it updates
  | { action: "none" } // nothing to restore, or the target is gone → the next landing rule
  | { action: "restore"; path: string };

// Decide how to restore a workspace's remembered chat given the currently-loaded
// roster. Pure so it's unit-testable (chatLandingPath is its caller). An empty channel list means nothing has loaded yet; the agent roster
// is fetched separately (daemon-forwarded) and reports itself via `agentsPending`.
//
// The critical invariant: a remembered channel OR agent that is NOT in the loaded
// roster must resolve to "none", NEVER "restore". A disconnected daemon drops its
// agent session from the roster; if we restored it, the agent route would bounce
// back to the landing, the landing would restore the dead agent again, and the app
// flickers forever. DMs restore optimistically — a human peer doesn't vanish on a
// daemon drop, and its route page handles a genuinely removed peer.
export function resolveLastChatRestore(
  workspaceId: string,
  last: LastChatRef | null,
  roster: RosterSnapshot,
  isHidden: (kind: LastChatKind, id: string) => boolean,
): RestoreDecision {
  if (!last) return { action: "none" };
  // A DM or group DM the user hid never comes back by landing (only opening it on purpose does).
  if (isHidden(last.kind, last.id)) return { action: "none" };
  if (last.kind === "channel" || last.kind === "agent") {
    if (roster.channels.length === 0) return { action: "wait" };
    const exists =
      last.kind === "channel"
        ? roster.channels.some((c) => c.id === last.id)
        : roster.agents.some((a) => a.agentId === last.id || a.cyboId === last.id);
    // An ABSENT agent can't be judged gone until its roster has settled — "not
    // fetched yet" and "removed from the roster" look identical, and only one of
    // them should skip the remembered chat. Checked after `exists` so a roster we
    // already hold still restores immediately instead of waiting on the daemon.
    if (!exists && last.kind === "agent" && roster.agentsPending) return { action: "wait" };
    if (!exists) return { action: "none" };
  }
  return { action: "restore", path: lastChatPath(workspaceId, last) };
}

/** How long a landing waits on the #onboarding answer before it falls back to a channel. */
export const ONBOARDING_WAIT_MS = 3_000;

/** Where "Chat" lands: the remembered chat if it is still there, else the person's active
 *  #onboarding, else the first visible channel. "wait" while the roster or the onboarding
 *  answer is still loading (cap: callers give up waiting after ONBOARDING_WAIT_MS and pass
 *  `loaded: true`). */
export function chatLandingPath(
  workspaceId: string,
  i: {
    last: LastChatRef | null;
    roster: RosterSnapshot;
    onboarding: { loaded: boolean; channelId: string | null; active: boolean };
    channels: readonly (Pick<Channel, "id" | "type" | "isHidden"> & { name?: string })[];
    isHidden: (kind: LastChatKind, id: string) => boolean;
  },
): { action: "wait" } | { action: "go"; path: string } {
  const restore = resolveLastChatRestore(workspaceId, i.last, i.roster, i.isHidden);
  if (restore.action === "wait") return restore;
  if (restore.action === "restore") return { action: "go", path: restore.path };
  if (!i.onboarding.loaded) return { action: "wait" };
  if (i.onboarding.active && i.onboarding.channelId) {
    return { action: "go", path: `/workspace/${workspaceId}/channel/${i.onboarding.channelId}` };
  }
  // #general first (leaving #onboarding lands there, X01), else the first channel; never a
  // group DM (hidden channels list under DMs).
  const visible = visibleChannels(i.channels);
  const first = visible.find((c) => c.name === "general") ?? visible[0];
  return first
    ? { action: "go", path: `/workspace/${workspaceId}/channel/${first.id}` }
    : { action: "wait" };
}
